import React, { useState, useEffect, useMemo } from 'react';
import { useTable } from 'react-table';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import BannerEditModal from '../components/BannerEditModal';
import AssignProductsModal from '../components/AssignProductsModal';

const AllBanner = () => {
  const [banners, setBanners] = useState([]);
  const [editBanner, setEditBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assignProductsBanner, setAssignProductsBanner] = useState(null);

  const fetchBannerImages = async () => {
    setLoading(true);
    try {
      const response = await fetch(SummaryApi.getBannerImages.url, {
        method: SummaryApi.getBannerImages.method,
        headers: {
          'Authorization': 'Bearer oakyaecom_no_units_916',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve banners');
      }

      const responseData = await response.json();

      if (responseData.status) {
        setBanners(responseData.data || []);
      } else {
        toast.error(responseData.message || 'Failed to load banners');
      }
    } catch (error) {
      toast.error('Error retrieving banners');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBannerImages();
  }, []);

  const handleEditClick = (banner) => {
    setEditBanner(banner);
  };

  const handleAssignProductsClick = (banner) => {
    setAssignProductsBanner(banner);
  };

  const handleCloseEdit = () => {
    setEditBanner(null);
  };

  const handleCloseAssignProducts = () => {
    setAssignProductsBanner(null);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Banner ID',
        accessor: 'banner_id',
        Cell: ({ value }) => <div className="font-medium text-gray-800">{value}</div>,
      },
      // {
      //   Header: 'Desktop Image',
      //   accessor: 'desktop_img',
      //   Cell: ({ value }) => (
      //     <div className="flex justify-center">
      //       <img
      //         src={`${SummaryApi.bannerImage.url}${value}`}
      //         alt="Desktop Banner"
      //         className="h-24 w-auto object-contain rounded shadow-sm border border-gray-200"
      //         onError={(e) => { e.target.src = 'https://via.placeholder.com/300x100?text=Image+Error'; }}
      //       />
      //     </div>
      //   ),
      // },
      {
        Header: 'Mobile Image',
        accessor: 'mobile_img',
        Cell: ({ value }) => (
          <div className="flex justify-center">
            <img
              src={`${SummaryApi.bannerImage.url}${value}`}
              alt="Mobile Banner"
              className="h-24 w-auto object-contain rounded shadow-sm border border-gray-200"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/150x100?text=Image+Error'; }}
            />
          </div>
        ),
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => (
          <div className="flex justify-center gap-3">
            <button
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition font-medium"
              onClick={() => handleEditClick(row.original)}
            >
              Update
            </button>
            {/* <button
              className="px-4 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition font-medium"
              onClick={() => handleAssignProductsClick(row.original)}
            >
              Assign Products
            </button> */}
          </div>
        ),
      },
    ],
    []
  );

const data = useMemo(
  () => banners
    .filter(banner => banner.is_active)
    .slice(0, 3),   // 👈 Only first 3 banners
  [banners]
);


  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data });

  const showEmptyState = !loading && data.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="bg-white shadow rounded-lg p-5 mb-6">
        <h2 className="font-bold text-xl text-gray-800">All Banners</h2>
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center h-64">
          <div className="border-4 border-blue-500 border-t-transparent rounded-full w-12 h-12 animate-spin"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading banners...</p>
        </div>
      ) : showEmptyState ? (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center bg-white rounded-lg shadow">
          <img
            src="https://img.freepik.com/free-vector/no-data-concept-illustration_114360-536.jpg"
            alt="No banners found"
            className="w-64 md:w-80 mb-6 opacity-90"
          />
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            No Active Banners Yet
          </h3>
          <p className="text-gray-500 mb-8 max-w-md">
            You haven't added any active banners. Upload new banners to get started.
          </p>
          {/* Optional: Add upload button if you have upload modal/component */}
          {/* <button className="bg-red-600 text-white py-2.5 px-6 rounded-full hover:bg-red-700 transition font-medium">
            Upload New Banner
          </button> */}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps()}
                      className="px-6 py-3 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider"
                    >
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="hover:bg-gray-50 transition">
                    {row.cells.map(cell => (
                      <td
                        {...cell.getCellProps()}
                        className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-800"
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editBanner && (
        <BannerEditModal
          banner={editBanner}
          onClose={handleCloseEdit}
          fetchData={fetchBannerImages}
        />
      )}

      {assignProductsBanner && (
        <AssignProductsModal
          banner={assignProductsBanner}
          onClose={handleCloseAssignProducts}
          fetchData={fetchBannerImages}
        />
      )}
    </div>
  );
};

export default AllBanner;